import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './button';

// Import custom icons - avoid lucide for main UI
import { 
  MairyIcon, 
  TreeOfLifeIcon,
  GoldenSpiralIcon 
} from './legendary-icons';

// Simple inline icons for auth UI only
const LogInIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
    <polyline points="10 17 15 12 10 7"/>
    <line x1="15" y1="12" x2="3" y2="12"/>
  </svg>
);

const UserIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const SaveIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
    <polyline points="17 21 17 13 7 13 7 21"/>
    <polyline points="7 3 7 8 15 8"/>
  </svg>
);

const ShieldIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

const AlertIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const CloseIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const FolderIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
  </svg>
);

const PlusIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const TrashIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

// ============================================================
// BELONGING SESSIONS SSO INTEGRATION
// Full integration with SSO validation and webhooks
// ============================================================

const BELONGING_SESSIONS_API = 'https://writing-forge.preview.emergentagent.com';
const BLISSABODE_WEBHOOK_KEY = 'bLiSsAbOdE_w3bH00k_K3y_2024_s3cur3';

// SSO Helper Functions
export const getBlissAbodeUser = () => {
  const userData = localStorage.getItem('blissabode_user');
  return userData ? JSON.parse(userData) : null;
};

export const isBlissAbodeSSOUser = () => {
  return localStorage.getItem('blissabode_sso_source') === 'belonging_sessions';
};

export const getUserName = () => {
  return localStorage.getItem('blissabode_user_name') || null;
};

export const getUserId = () => {
  return localStorage.getItem('blissabode_user_id') || null;
};

export const getUserEmail = () => {
  return localStorage.getItem('blissabode_user_email') || null;
};

export const isLoggedIn = () => {
  return !!localStorage.getItem('blissabode_user_id');
};

export const blissabodeLogout = () => {
  localStorage.removeItem('blissabode_user');
  localStorage.removeItem('blissabode_user_id');
  localStorage.removeItem('blissabode_user_name');
  localStorage.removeItem('blissabode_user_email');
  localStorage.removeItem('blissabode_sso_source');
  window.dispatchEvent(new CustomEvent('blissabode-logout'));
};

// Set user session (works for both SSO and email/password)
export const setUserSession = (user, source = 'email') => {
  localStorage.setItem('blissabode_user', JSON.stringify(user));
  localStorage.setItem('blissabode_user_id', user.id);
  localStorage.setItem('blissabode_user_name', user.name);
  if (user.email) localStorage.setItem('blissabode_user_email', user.email);
  localStorage.setItem('blissabode_sso_source', source);
  window.dispatchEvent(new CustomEvent('blissabode-sso-login', { detail: user }));
};

// SSO Validation Function
export const validateSSOToken = async (bs_user, bs_token, bs_time) => {
  try {
    const response = await fetch(`${BELONGING_SESSIONS_API}/api/sso/validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Source-App': 'blissabode'
      },
      body: JSON.stringify({ bs_user, bs_token, bs_time })
    });
    
    const data = await response.json();
    
    if (data.valid && data.user) {
      setUserSession(data.user, 'belonging_sessions');
      return { success: true, user: data.user };
    } else {
      return { success: false, message: data.message || 'Invalid token' };
    }
  } catch (error) {
    console.error('SSO validation error:', error);
    return { success: false, message: 'Connection error' };
  }
};

// Register new user
export const registerUser = async (email, name, password = null) => {
  try {
    const response = await fetch(`${BELONGING_SESSIONS_API}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, password })
    });
    
    const data = await response.json();
    
    if (data.success && data.user) {
      setUserSession(data.user, 'email');
      return { success: true, user: data.user };
    }
    return { success: false, message: data.message || 'Registration failed' };
  } catch (error) {
    console.error('Registration error:', error);
    return { success: false, message: 'Connection error' };
  }
};

// Login with email/password
export const loginUser = async (email, password) => {
  try {
    const response = await fetch(`${BELONGING_SESSIONS_API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await response.json();
    
    if (data.success && data.user) {
      setUserSession(data.user, 'email');
      return { success: true, user: data.user };
    }
    return { success: false, message: data.message || 'Login failed' };
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, message: 'Connection error' };
  }
};

// Get user's projects
export const getUserProjects = async (userId, projectType = null) => {
  try {
    let url = `${BELONGING_SESSIONS_API}/api/users/${userId}/projects`;
    if (projectType) url += `?project_type=${projectType}`;
    
    const response = await fetch(url);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching projects:', error);
    return { projects: [], total: 0 };
  }
};

// Create new project for user
export const createUserProject = async (userId, projectData) => {
  try {
    const response = await fetch(`${BELONGING_SESSIONS_API}/api/users/${userId}/projects`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(projectData)
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating project:', error);
    return null;
  }
};

// Delete project
export const deleteUserProject = async (userId, projectId) => {
  try {
    const response = await fetch(`${BELONGING_SESSIONS_API}/api/users/${userId}/projects/${projectId}`, {
      method: 'DELETE'
    });
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting project:', error);
    return { success: false };
  }
};

// Webhook Functions - Sync data to Belonging Sessions
export const syncActivityToBelongingSessions = async (userId, activityType, data) => {
  if (!userId) return null;
  
  try {
    const response = await fetch(`${BELONGING_SESSIONS_API}/api/webhooks/blissabode/activity`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Webhook-Key': BLISSABODE_WEBHOOK_KEY
      },
      body: JSON.stringify({
        user_id: userId,
        activity_type: activityType,
        data: data,
        timestamp: new Date().toISOString()
      })
    });
    
    const result = await response.json();
    console.log('✅ Activity synced to Belonging Sessions:', result);
    return result;
  } catch (error) {
    console.error('❌ Failed to sync activity:', error);
    return null;
  }
};

// Sync charter progress to Belonging Sessions
export const syncCharterProgress = async (userId, projectId, projectName, progress, sections) => {
  return syncActivityToBelongingSessions(userId, 'charter_progress', {
    project_id: projectId,
    project_name: projectName,
    progress_percentage: progress,
    sections_completed: sections,
    updated_at: new Date().toISOString()
  });
};

// Sync startup plan progress to Belonging Sessions
export const syncStartupProgress = async (userId, planName, progress, sections) => {
  return syncActivityToBelongingSessions(userId, 'startup_progress', {
    plan_name: planName,
    progress_percentage: progress,
    sections_completed: sections,
    updated_at: new Date().toISOString()
  });
};

// ============================================================
// SIGN IN BANNER COMPONENT
// Shows prompt to sign in to save work
// ============================================================

export const SignInBanner = ({ 
  onSignIn, 
  onDismiss,
  variant = 'default', // 'default', 'minimal', 'urgent'
  showClose = true,
  className = ''
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userName, setUserName] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    const checkAuth = () => {
      const name = localStorage.getItem('blissabode_user_name');
      setUserName(name);
      setIsLoggedIn(!!name);
    };
    
    checkAuth();
    
    // Listen for login/logout events
    window.addEventListener('blissabode-sso-login', checkAuth);
    window.addEventListener('blissabode-logout', checkAuth);
    window.addEventListener('storage', checkAuth);
    
    return () => {
      window.removeEventListener('blissabode-sso-login', checkAuth);
      window.removeEventListener('blissabode-logout', checkAuth);
      window.removeEventListener('storage', checkAuth);
    };
  }, []);
  
  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };
  
  // Don't show if logged in or dismissed
  if (isLoggedIn || dismissed) return null;
  
  if (variant === 'minimal') {
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`flex items-center gap-2 text-xs text-amber-400/80 ${className}`}
      >
        <AlertIcon size={12} />
        <span>Sign in to save your work</span>
        <button 
          onClick={onSignIn}
          className="underline hover:text-amber-300"
        >
          Sign in
        </button>
      </motion.div>
    );
  }
  
  if (variant === 'urgent') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`bg-red-500/20 border border-red-500/40 rounded-xl p-4 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <ShieldIcon size={20} className="text-red-400" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-red-300 mb-1">Your work isn't saved!</h4>
            <p className="text-xs text-red-300/70 mb-3">
              Create a free account or sign in with Belonging Sessions to save your progress and access it from any device.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={onSignIn}
                className="bg-red-500 hover:bg-red-600 text-white text-xs h-8"
              >
                <LogInIcon size={12} className="mr-1" />
                Sign In to Save
              </Button>
              {showClose && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleDismiss}
                  className="text-red-300/70 hover:text-red-300 text-xs h-8"
                >
                  Continue without saving
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }
  
  // Default variant
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 ${className}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <SaveIcon size={14} className="text-amber-400" />
          </div>
          <div>
            <p className="text-sm text-amber-300 font-medium">Create an account to save</p>
            <p className="text-xs text-amber-300/60">Your work will be preserved across sessions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={onSignIn}
            className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 text-xs h-8"
          >
            <LogInIcon size={12} className="mr-1" />
            Sign In
          </Button>
          {showClose && (
            <button
              onClick={handleDismiss}
              className="text-amber-400/50 hover:text-amber-400 p-1"
            >
              <CloseIcon size={14} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// ============================================================
// USER STATUS INDICATOR
// Shows logged in user or sign in prompt
// ============================================================

export const UserStatusIndicator = ({ onSignIn, onLogout, compact = false }) => {
  const [user, setUser] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  
  useEffect(() => {
    const checkAuth = () => {
      const userData = localStorage.getItem('blissabode_user');
      const userName = localStorage.getItem('blissabode_user_name');
      if (userData) {
        setUser(JSON.parse(userData));
      } else if (userName) {
        setUser({ name: userName });
      } else {
        setUser(null);
      }
    };
    
    checkAuth();
    
    window.addEventListener('blissabode-sso-login', checkAuth);
    window.addEventListener('blissabode-logout', checkAuth);
    window.addEventListener('storage', checkAuth);
    
    return () => {
      window.removeEventListener('blissabode-sso-login', checkAuth);
      window.removeEventListener('blissabode-logout', checkAuth);
      window.removeEventListener('storage', checkAuth);
    };
  }, []);
  
  if (!user) {
    if (compact) {
      return (
        <button
          onClick={onSignIn}
          className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors"
        >
          <LogInIcon size={14} />
          <span>Sign in</span>
        </button>
      );
    }
    
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={onSignIn}
        className="text-white/60 hover:text-white hover:bg-white/10"
      >
        <LogInIcon size={14} className="mr-1.5" />
        Sign In
      </Button>
    );
  }
  
  const initials = user.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';
  
  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-xs font-bold text-white">
          {initials}
        </div>
        {!compact && <span className="text-sm hidden sm:inline">{user.name}</span>}
      </button>
      
      <AnimatePresence>
        {showMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute right-0 top-full mt-2 bg-slate-800 border border-slate-700 rounded-xl shadow-xl overflow-hidden min-w-[160px] z-50"
          >
            <div className="p-3 border-b border-slate-700">
              <p className="text-sm font-medium text-white">{user.name}</p>
              {user.email && <p className="text-xs text-white/50">{user.email}</p>}
              {isBlissAbodeSSOUser() && (
                <p className="text-[10px] text-emerald-400 mt-1">Belonging Sessions</p>
              )}
            </div>
            <button
              onClick={() => {
                blissabodeLogout();
                onLogout?.();
                setShowMenu(false);
              }}
              className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-slate-700/50 transition-colors"
            >
              Sign Out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Hook to use SSO state
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const checkAuth = () => {
      const userData = localStorage.getItem('blissabode_user');
      const userName = localStorage.getItem('blissabode_user_name');
      
      if (userData) {
        setUser(JSON.parse(userData));
        setIsAuthenticated(true);
      } else if (userName) {
        setUser({ name: userName });
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    };
    
    checkAuth();
    
    // Check for SSO params in URL
    const params = new URLSearchParams(window.location.search);
    const bs_user = params.get('bs_user');
    const bs_token = params.get('bs_token');
    const bs_time = params.get('bs_time');
    
    if (bs_user && bs_token && bs_time) {
      validateSSOToken(bs_user, bs_token, bs_time).then(result => {
        if (result.success) {
          setUser(result.user);
          setIsAuthenticated(true);
          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
        }
        setLoading(false);
      });
    }
    
    window.addEventListener('blissabode-sso-login', checkAuth);
    window.addEventListener('blissabode-logout', checkAuth);
    window.addEventListener('storage', checkAuth);
    
    return () => {
      window.removeEventListener('blissabode-sso-login', checkAuth);
      window.removeEventListener('blissabode-logout', checkAuth);
      window.removeEventListener('storage', checkAuth);
    };
  }, []);
  
  return { 
    isAuthenticated, 
    user, 
    loading, 
    logout: blissabodeLogout,
    userId: getUserId(),
    userName: getUserName(),
    userEmail: getUserEmail()
  };
};

// ============================================================
// AUTH MODAL COMPONENT
// Complete sign in / register flow
// ============================================================

export const AuthModal = ({ isOpen, onClose, onSuccess, initialMode = 'signin' }) => {
  const [mode, setMode] = useState(initialMode); // 'signin', 'register', 'sso'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ssoUrl, setSsoUrl] = useState('');
  
  useEffect(() => {
    // Fetch SSO URL
    const fetchSSOUrl = async () => {
      try {
        const response = await fetch(`${BELONGING_SESSIONS_API}/api/sso/login-url`);
        const data = await response.json();
        setSsoUrl(data.login_url);
      } catch (err) {
        console.error('Error fetching SSO URL:', err);
      }
    };
    
    if (isOpen) {
      fetchSSOUrl();
      setError('');
    }
  }, [isOpen]);
  
  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const result = await loginUser(email, password);
    
    if (result.success) {
      onSuccess?.(result.user);
      onClose();
    } else {
      setError(result.message || 'Sign in failed');
    }
    setLoading(false);
  };
  
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!name.trim()) {
      setError('Please enter your name');
      setLoading(false);
      return;
    }
    
    const result = await registerUser(email, name, password);
    
    if (result.success) {
      onSuccess?.(result.user);
      onClose();
    } else {
      setError(result.message || 'Registration failed');
    }
    setLoading(false);
  };
  
  const handleSSOLogin = () => {
    if (ssoUrl) {
      window.location.href = ssoUrl;
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-md bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-white/10 shadow-2xl overflow-hidden"
        >
          {/* Header */}
          <div className="relative p-6 pb-4 border-b border-white/10">
            <div className="flex items-center justify-center gap-3 mb-2">
              <GoldenSpiralIcon size={32} className="text-amber-400" />
            </div>
            <h2 className="text-xl font-heading text-white text-center">
              {mode === 'signin' ? 'Welcome Back' : mode === 'register' ? 'Create Account' : 'Sign In with Belonging Sessions'}
            </h2>
            <p className="text-white/50 text-sm text-center mt-1">
              {mode === 'signin' ? 'Sign in to access your projects' : 
               mode === 'register' ? 'Join to save your work securely' : 
               'Connect with your Belonging Sessions account'}
            </p>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-white/40 hover:text-white/80 transition-colors"
            >
              <CloseIcon size={18} />
            </button>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertIcon size={16} className="text-red-400" />
                <span className="text-sm text-red-300">{error}</span>
              </div>
            )}
            
            {/* SSO Option */}
            <Button
              onClick={handleSSOLogin}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white"
              disabled={!ssoUrl}
            >
              <TreeOfLifeIcon size={18} className="mr-2" />
              Continue with Belonging Sessions
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-2 bg-slate-800 text-white/40">or use email</span>
              </div>
            </div>
            
            {/* Email Form */}
            <form onSubmit={mode === 'signin' ? handleEmailSignIn : handleRegister} className="space-y-3">
              {mode === 'register' && (
                <input
                  type="text"
                  placeholder="Your Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                  required
                />
              )}
              <input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                required
              />
              <input
                type="password"
                placeholder={mode === 'register' ? 'Create Password (optional)' : 'Password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {mode === 'signin' ? 'Signing In...' : 'Creating Account...'}
                  </span>
                ) : (
                  <>
                    {mode === 'signin' ? 'Sign In' : 'Create Account'}
                  </>
                )}
              </Button>
            </form>
            
            {/* Toggle Mode */}
            <p className="text-center text-sm text-white/50">
              {mode === 'signin' ? (
                <>
                  Don't have an account?{' '}
                  <button onClick={() => setMode('register')} className="text-amber-400 hover:underline">
                    Create one
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button onClick={() => setMode('signin')} className="text-amber-400 hover:underline">
                    Sign in
                  </button>
                </>
              )}
            </p>
            
            {/* Security Note */}
            <div className="flex items-start gap-2 pt-2 text-xs text-white/40">
              <ShieldIcon size={14} className="flex-shrink-0 mt-0.5" />
              <span>Your data is securely stored and synced with Belonging Sessions for seamless access across all Bliss Abode experiences.</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============================================================
// PROJECTS MANAGER COMPONENT
// Shows user's saved projects with CRUD operations
// ============================================================

export const ProjectsManager = ({ 
  isOpen, 
  onClose, 
  onSelectProject, 
  onNewProject,
  currentProjectId = null,
  projectType = null // 'community', 'startup', 'tome', or null for all
}) => {
  const { userId, userName } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectType, setNewProjectType] = useState(projectType || 'community');
  
  useEffect(() => {
    if (isOpen && userId) {
      loadProjects();
    }
  }, [isOpen, userId, projectType]);
  
  const loadProjects = async () => {
    setLoading(true);
    const data = await getUserProjects(userId, projectType);
    setProjects(data.projects || []);
    setLoading(false);
  };
  
  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    
    const result = await createUserProject(userId, {
      name: newProjectName,
      project_type: newProjectType,
      user_id: userId,
      user_name: userName,
      user_email: getUserEmail()
    });
    
    if (result && result.id) {
      setShowNewForm(false);
      setNewProjectName('');
      loadProjects();
      onNewProject?.(result);
    }
  };
  
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This cannot be undone.')) return;
    
    setDeleting(projectId);
    const result = await deleteUserProject(userId, projectId);
    
    if (result.success) {
      loadProjects();
    }
    setDeleting(null);
  };
  
  const getProjectTypeLabel = (type) => {
    switch (type) {
      case 'startup': return 'Startup Studio';
      case 'tome': return 'Tome Machine';
      case 'community': return 'Community Charter';
      default: return type;
    }
  };
  
  const getProjectTypeColor = (type) => {
    switch (type) {
      case 'startup': return 'text-indigo-400 bg-indigo-500/20';
      case 'tome': return 'text-fuchsia-400 bg-fuchsia-500/20';
      case 'community': return 'text-emerald-400 bg-emerald-500/20';
      default: return 'text-white/60 bg-white/10';
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-2xl max-h-[80vh] bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FolderIcon size={24} className="text-amber-400" />
              <div>
                <h2 className="text-xl font-heading text-white">Your Projects</h2>
                <p className="text-sm text-white/50">{projects.length} project{projects.length !== 1 ? 's' : ''} saved</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => setShowNewForm(true)}
                className="bg-amber-600 hover:bg-amber-500 text-white"
              >
                <PlusIcon size={16} className="mr-1" />
                New Project
              </Button>
              <button
                onClick={onClose}
                className="p-2 text-white/40 hover:text-white/80 transition-colors"
              >
                <CloseIcon size={18} />
              </button>
            </div>
          </div>
          
          {/* New Project Form */}
          <AnimatePresence>
            {showNewForm && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-b border-white/10 overflow-hidden"
              >
                <div className="p-4 bg-white/5 space-y-3">
                  <input
                    type="text"
                    placeholder="Project Name"
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    className="w-full px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                    autoFocus
                  />
                  <div className="flex items-center gap-2">
                    <select
                      value={newProjectType}
                      onChange={(e) => setNewProjectType(e.target.value)}
                      className="flex-1 px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                    >
                      <option value="community">Community Charter</option>
                      <option value="startup">Startup Studio</option>
                      <option value="tome">Tome Machine</option>
                    </select>
                    <Button size="sm" onClick={handleCreateProject} className="bg-emerald-600 hover:bg-emerald-500">
                      Create
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowNewForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Projects List */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-12">
                <FolderIcon size={48} className="text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No projects yet</p>
                <p className="text-sm text-white/30 mt-1">Create your first project to get started</p>
              </div>
            ) : (
              <div className="space-y-2">
                {projects.map((project) => (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 rounded-xl border transition-all cursor-pointer ${
                      project.id === currentProjectId 
                        ? 'bg-amber-500/10 border-amber-500/30' 
                        : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20'
                    }`}
                    onClick={() => onSelectProject(project)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-white truncate">{project.name}</h3>
                          {project.id === currentProjectId && (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">Current</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className={`px-2 py-0.5 rounded ${getProjectTypeColor(project.project_type)}`}>
                            {getProjectTypeLabel(project.project_type)}
                          </span>
                          <span className="text-white/40">
                            {project.completion}% complete
                          </span>
                          <span className="text-white/30">
                            Updated {new Date(project.updated_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project.id);
                        }}
                        disabled={deleting === project.id}
                        className="p-2 text-white/30 hover:text-red-400 transition-colors"
                      >
                        {deleting === project.id ? (
                          <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                        ) : (
                          <TrashIcon size={16} />
                        )}
                      </button>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-3 h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all ${
                          project.project_type === 'startup' ? 'bg-indigo-500' :
                          project.project_type === 'tome' ? 'bg-fuchsia-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${project.completion}%` }}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SignInBanner;
